import { useState } from 'react';
import { Tabs, TabPanel } from '@/ui/Tabs';
import { BarraDelCentro } from '@/components/BarraDelCentro';
import { Compositor } from './Compositor';
import { Historial } from './Historial';
import { Plantillas } from './Plantillas';

/**
 * MensajesDirectos.tsx — la pantalla de `#notificaciones`.
 *
 * ## Es la pantalla que no tiene entrada en el menú
 *
 * Al módulo se entra siempre por Comunicaciones (`index.html:389` es su único
 * enlace). A esta se llega pulsando «Mensajes directos» en la barra de arriba.
 * Eso no estaba escrito en ninguna parte hasta el 2026-09-15.
 *
 * ## Las pestañas vienen de Radix, y eso no es decoración
 *
 * El vanilla eran tres `<button class="tab-btn">` con una clase `.active`: un
 * lector de pantalla no anunciaba que fueran pestañas ni cuántas había, y las
 * flechas no hacían nada. `ui/Tabs` trae el patrón WAI-ARIA completo.
 *
 * Y su `showTab()` empezaba borrando `.active` de **todos** los `.tab-btn` del
 * documento, lo que apagaba también la barra del módulo. Aquí son dos
 * componentes distintos —`BarraDelCentro` son enlaces entre rutas, estas son
 * pestañas dentro de una vista— y ninguno puede pisar al otro.
 *
 * ## `#notificaciones/<pestaña>` abre la que se pida
 *
 * Mismo mecanismo que Perfil y Solicitudes: `window._routeParams`, leído **una
 * vez** al montar. Si se leyera en cada render, cambiar de pestaña volvería a
 * la de la URL en el siguiente repintado.
 */

const PESTANAS = [
  { clave: 'nueva', etiqueta: 'Nueva notificación', icono: 'bi-envelope' },
  { clave: 'historial', etiqueta: 'Historial', icono: 'bi-list-check' },
  { clave: 'plantillas', etiqueta: 'Plantillas', icono: 'bi-palette2' },
];

const CLAVES = new Set(PESTANAS.map((p) => p.clave));

export function MensajesDirectos() {
  const [pestana, setPestana] = useState(() => {
    const p = window._routeParams?.[0];
    return p && CLAVES.has(p) ? p : 'nueva';
  });

  function cambiar(v: string) {
    setPestana(v);
    // La sub-ruta se refleja en la barra de direcciones, así que una pestaña
    // concreta se puede enlazar y recargar. `replaceState` y no `navigate()`:
    // esto no cambia de pantalla, solo dice en cuál de sus tres partes estás.
    history.replaceState(null, '', v === 'nueva' ? '#notificaciones' : `#notificaciones/${v}`);
  }

  return (
    <>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
          <i className="bi bi-megaphone" aria-hidden="true" />
          Centro de Notificaciones
        </h1>
        <p className="mt-1 text-3 text-muted">
          Comunicados multicanal y mensajes directos, en un solo lugar.
        </p>
      </header>

      <BarraDelCentro activa="mensajes" />

      <Tabs pestanas={PESTANAS} valor={pestana} onCambio={cambiar}>
        <TabPanel clave="nueva">
          {/* Al enviar se salta al historial, que es donde se ve el resultado
              —lo mismo que hacía el vanilla con `showTab('history')`—. */}
          <Compositor alEnviar={() => cambiar('historial')} />
        </TabPanel>
        <TabPanel clave="historial"><Historial /></TabPanel>
        <TabPanel clave="plantillas"><Plantillas /></TabPanel>
      </Tabs>
    </>
  );
}
